// components/asset-management/scenario-engine.ts
// Institutional asset-management underwriting engine for the Asset (Property) view.
//
// Anchors a repositioning business plan to the asset's LIVE data: it reads the
// property's units (area, asking rent, lease status) to build a lease-by-lease
// rent roll (passing rent, ERV, reversion, lease expiry, WAULT), then projects a
// full hold-period model off that baseline — a multi-year operating cash flow
// (GPR → vacancy → opex → NOI → capex → debt service → levered CF), a layered
// capital stack (senior + optional mezzanine) with covenant tracking, an exit on
// a stabilised cap rate, an equity distribution waterfall (return of capital →
// preferred → promote) and the headline returns (unlevered/levered IRR, equity
// multiple, profit-on-cost, yield-on-cost). All pure functions over inventory
// DTOs; no API/backend. Every coefficient is an explicit, editable assumption —
// see ScenarioParams / deriveDefaultParams.

import type { LeasingPropertyDTO, LeasingUnitDTO } from '#types/types';

import { irr as computeIrr } from './finance';

interface Point {
  x: number;
  y: number;
}

export type TenantMix = 'retail' | 'mixed' | 'office' | 'leisure';
export type Sustainability = 'baseline' | 'silver' | 'gold' | 'net-zero';

/** Every editable input to the model. The agent edits these; the grid recomputes. */
export interface ScenarioParams {
  // --- Strategy levers ---
  capexEnvelope: number; // € of capital deployed across the works
  tenantMix: TenantMix;
  rentUpliftPct: number; // target ERV uplift vs current passing rent, %
  timelineMonths: number; // months of works to practical completion
  sustainability: Sustainability;
  // --- Market / operating ---
  holdYears: number; // years to exit (disposal)
  rentGrowthPct: number; // ERV growth per year post-reposition, %
  opexRatioPct: number; // non-recoverable opex as % of gross rent
  // --- Exit / transaction ---
  exitCapRatePct: number; // exit yield applied to stabilised NOI, %
  acqCostPct: number; // acquisition costs, % of purchase price
  sellingCostPct: number; // disposal costs, % of gross sale (GDV)
  // --- Senior debt ---
  ltvPct: number; // senior leverage as % of total cost (LTC), %
  interestRatePct: number; // senior all-in rate, %
  arrangementFeePct: number; // senior arrangement fee, % of loan
  // --- Mezzanine (optional; 0 disables) ---
  mezzLtvPct: number; // mezzanine as % of total cost, %
  mezzRatePct: number; // mezzanine rate, %
  // --- Equity waterfall ---
  prefRatePct: number; // LP preferred return (hurdle), %
  promotePct: number; // GP promote / carry above the hurdle, %
}

/** A single tenancy in the rent roll. */
interface RentRollLine {
  unitId: string;
  label: string; // unit identifier
  tenant: string;
  areaSqm: number;
  passingRent: number; // € / yr (0 when vacant)
  passingPerSqm: number; // € / m² / yr
  ervPerSqm: number; // € / m² / yr (market)
  erv: number; // € / yr at market
  status: string;
  isOccupied: boolean;
  leaseExpiryYear: number; // years from now until expiry (0 when vacant)
  reversionPct: number; // ERV vs passing, % (uplift on re-let)
}

/** One year of the operating + financing cash flow. */
interface AnnualCF {
  year: number;
  occupancyPct: number;
  rentPerSqm: number; // achieved €/m² (blended)
  gpr: number; // gross potential rent (€) at full occupancy
  vacancyLoss: number; // € lost to vacancy (shown positive)
  opex: number; // € non-recoverable opex (shown positive)
  noi: number; // € net operating income
  capex: number; // € capital spend (shown positive)
  noiAfterCapex: number; // € NOI − capex
  interest: number; // € total debt interest (senior + mezz, shown positive)
  debtFees: number; // € arrangement fees (year 0, shown positive)
  unlevered: number; // € unlevered net cash flow
  levered: number; // € levered net cash flow to equity
  icr: number; // interest cover ratio (NOI / interest), × — 0 if no debt
}

/** Result of running the equity distribution waterfall. */
interface WaterfallResult {
  lpContributions: number; // € equity drawn from LP
  returnOfCapital: number; // € capital returned to LP
  prefPaid: number; // € preferred return paid to LP
  residualToLp: number; // € residual profit to LP after promote
  gpPromote: number; // € carried interest to GP
  lpDistributions: number; // € total to LP (capital + pref + residual)
  lpIrrPct: number; // LP net IRR, %
  lpMultiple: number; // LP net equity multiple, ×
}

export interface ScenarioOutcomes {
  occupancyCurve: Point[]; // x = month, y = occupancy %
  annualCashflow: AnnualCF[]; // year-by-year operating + financing cash flow
  holdYears: number;
  // headline operating
  irrPct: number; // unlevered project IRR, %
  leveredIrrPct: number; // levered (equity) IRR, %
  timeToDeliveryMonths: number;
  stabilizedOccupancyPct: number;
  stabilizedNoi: number;
  currentNoi: number;
  // cost & value
  totalProjectCost: number; // € all-in (purchase + acq + capex + fees)
  acquisitionCost: number; // €
  subtotalCost: number; // € (purchase + acquisition + capex, before fees)
  arrangementFee: number; // € senior + mezz fees
  exitValue: number; // € gross development value at exit
  netExitValue: number; // € after disposal costs
  profitEur: number; // € (exit value − total project cost)
  profitOnCostPct: number; // %
  yieldOnCostPct: number; // %
  // capital stack
  equity: number; // € equity required (peak)
  peakDebt: number; // € total debt (senior + mezz)
  peakDebtSenior: number; // €
  peakDebtMezz: number; // €
  ltcPct: number; // day-one loan-to-cost, %
  exitLtvPct: number; // debt / exit value, %
  minIcr: number; // worst-year interest cover, ×
  annualInterest: number; // € total stabilised interest
  equityMultiple: number; // levered equity multiple, ×
  // reversion / lease metrics
  waultYears: number; // weighted average unexpired lease term
  reversionPct: number; // portfolio ERV vs passing, %
  // distribution waterfall
  waterfall: WaterfallResult;
}

/** Live snapshot of the asset, derived from its real units + occupancy. */
export interface AssetBaseline {
  currentNoi: number; // € / year (net operating income)
  passingRentRoll: number; // € / year (gross, occupied units)
  currentOccupancyPct: number; // %
  totalAreaSqm: number;
  occupiedAreaSqm: number;
  unitCount: number;
  occupiedUnitCount: number;
  /** assumed in-place cap rate used to value current NOI, %. */
  marketCapRatePct: number;
  /** € capital value of the asset today (currentNoi / cap rate). */
  currentValue: number;
  /** € estimated rental value at full occupancy (market). */
  ervAnnual: number;
  /** lease-by-lease rent roll built from the asset's units. */
  rentRoll: RentRollLine[];
  /** weighted average unexpired lease term, years. */
  waultYears: number;
  /** portfolio reversion: ERV vs passing rent, %. */
  reversionPct: number;
  /** true when the rent roll was estimated from GLA (no asking rents on file). */
  rentRollIsEstimated: boolean;
  /** true once the asset has usable rent/area data (else the engine estimates). */
  hasFinancials: boolean;
}

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));
const sCurve = (t: number, steep = 9, mid = 0.45): number => 1 / (1 + Math.exp(-steep * (t - mid)));

export const fmtEuroM = (n: number): string => `€${(n / 1_000_000).toFixed(1)}M`;

const TENANT_MIX_META: Record<
  TenantMix,
  { label: string; targetOcc: number; absorption: number; noiYield: number }
> = {
  retail: { label: 'Retail-led', targetOcc: 92, absorption: 1.0, noiYield: 0.115 },
  mixed: { label: 'Mixed-use', targetOcc: 94, absorption: 1.15, noiYield: 0.125 },
  office: { label: 'Office-led', targetOcc: 88, absorption: 0.8, noiYield: 0.1 },
  leisure: { label: 'Leisure-led', targetOcc: 90, absorption: 0.95, noiYield: 0.108 },
};

const SUSTAINABILITY_META: Record<
  Sustainability,
  { label: string; occBonus: number; irrBonus: number; capexMult: number }
> = {
  baseline: { label: 'Baseline', occBonus: 0, irrBonus: 0, capexMult: 1.0 },
  silver: { label: 'BREEAM Silver', occBonus: 2, irrBonus: 0.4, capexMult: 1.05 },
  gold: { label: 'BREEAM Gold', occBonus: 4, irrBonus: 0.7, capexMult: 1.12 },
  'net-zero': { label: 'Net Zero Carbon', occBonus: 7, irrBonus: 1.1, capexMult: 1.22 },
};

// --- Underwriting assumptions used to derive the live baseline ---------------
/** €/m²/yr used to estimate rent when units carry no asking rent. */
const FALLBACK_RENT_PER_SQM = 240;
/** €/m² used to size the default capex envelope (refurb/repositioning, not redevelopment). */
const REFURB_COST_PER_SQM = 500;
/** Assumed in-place cap rate used to value the asset's current NOI. */
const MARKET_CAP_RATE = 0.06;
/** €/m²/yr market rent used to estimate ERV at full occupancy. */
const MARKET_RENT_PER_SQM = 260;
/** Default opex ratio used to convert the gross rent roll into NOI for the baseline. */
const BASELINE_OPEX_RATIO = 0.2;
/** Lease statuses treated as income-producing (occupied). */
const INCOME_STATUSES = new Set(['LEASED', 'EXPIRING_SOON', 'REPLACEMENT_UNDER_OFFER']);

/** Deterministic small hash from a string, used for stable synthetic lease expiries. */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Synthesise a lease expiry (years from now) from the unit's status. Real lease
 * dates aren't on the Unit DTO, so we anchor to status and spread deterministically
 * by unit id to produce a realistic WAULT instead of a single flat number.
 */
function leaseExpiryFromStatus(status: string, unitId: string): number {
  const h = hashString(unitId);
  if (status === 'EXPIRING_SOON') return 0.3 + (h % 7) / 10; // 0.3–0.9y
  if (status === 'REPLACEMENT_UNDER_OFFER') return 1 + (h % 12) / 10; // 1.0–2.1y
  // standard leases: spread across a 3–9 year band
  return 3 + (h % 60) / 10; // 3.0–8.9y
}

/**
 * IRR of an annual cash-flow series, from the shared procedure. The scenario
 * outcomes are always finite percentages, so "no real IRR" (NaN) reads as 0 here
 * — the convention this engine already used when the series had no sign change.
 */
function irr(cf: number[]): number {
  const rate = computeIrr(cf);
  return Number.isFinite(rate) ? rate : 0;
}

/**
 * European (whole-deal) equity distribution waterfall.
 * Tiers per period: (1) accrued preferred return, (2) return of capital,
 * (3) residual split with GP promote. Contributions accrue the preferred hurdle.
 */
function runWaterfall(equityCF: number[], prefRatePct: number, promotePct: number): WaterfallResult {
  const pref = clamp(prefRatePct, 0, 30) / 100;
  const promote = clamp(promotePct, 0, 50) / 100;
  let capitalAccount = 0; // outstanding LP capital
  let prefAccrued = 0; // unpaid preferred return
  let lpContributions = 0;
  let returnOfCapital = 0;
  let prefPaid = 0;
  let residualToLp = 0;
  let gpPromote = 0;
  const lpCF: number[] = [];

  for (let t = 0; t < equityCF.length; t += 1) {
    const v = equityCF[t] ?? 0;
    if (t > 0) prefAccrued += capitalAccount * pref; // accrue hurdle on outstanding capital
    if (v < 0) {
      // LP contribution
      const draw = -v;
      capitalAccount += draw;
      lpContributions += draw;
      lpCF.push(v);
      continue;
    }
    if (v <= 0) {
      lpCF.push(0);
      continue;
    }
    let cash = v;
    let lpYear = 0;
    // tier 1 — pay accrued preferred return
    const payPref = Math.min(cash, prefAccrued);
    prefAccrued -= payPref;
    cash -= payPref;
    prefPaid += payPref;
    lpYear += payPref;
    // tier 2 — return capital
    const payCap = Math.min(cash, capitalAccount);
    capitalAccount -= payCap;
    cash -= payCap;
    returnOfCapital += payCap;
    lpYear += payCap;
    // tier 3 — residual split (GP earns promote)
    const gp = cash * promote;
    const lp = cash - gp;
    gpPromote += gp;
    residualToLp += lp;
    lpYear += lp;
    lpCF.push(lpYear);
  }

  const lpDistributions = returnOfCapital + prefPaid + residualToLp;
  const lpIrrPct = clamp(irr(lpCF) * 100, -99, 200);
  const lpMultiple = lpContributions > 0 ? lpDistributions / lpContributions : 0;
  return {
    lpContributions: Math.round(lpContributions),
    returnOfCapital: Math.round(returnOfCapital),
    prefPaid: Math.round(prefPaid),
    residualToLp: Math.round(residualToLp),
    gpPromote: Math.round(gpPromote),
    lpDistributions: Math.round(lpDistributions),
    lpIrrPct: Math.round(lpIrrPct * 10) / 10,
    lpMultiple: Math.round(lpMultiple * 100) / 100,
  };
}

/** Derive the live baseline (rent roll, NOI, occupancy, WAULT) from the asset's real units. */
export function deriveAssetBaseline(
  property: LeasingPropertyDTO | undefined | null,
  units: ReadonlyArray<LeasingUnitDTO | undefined>,
): AssetBaseline {
  const liveUnits = units.filter((u): u is LeasingUnitDTO => Boolean(u));

  let totalAreaSqm = 0;
  let occupiedAreaSqm = 0;
  let passingRentRoll = 0;
  let occupiedCount = 0;

  for (const u of liveUnits) {
    const area = typeof u.areaSqm === 'number' && u.areaSqm > 0 ? u.areaSqm : 0;
    totalAreaSqm += area;
    const occupied = typeof u.status === 'string' && INCOME_STATUSES.has(u.status);
    if (occupied) {
      occupiedCount += 1;
      occupiedAreaSqm += area;
      if (typeof u.askingRent === 'number' && u.askingRent > 0) passingRentRoll += u.askingRent;
    }
  }

  // Occupancy: prefer the property's reported rate, then derive from units.
  const reportedOcc = typeof property?.occupancyRate === 'number' ? property.occupancyRate : null;
  const areaOcc = totalAreaSqm > 0 ? (occupiedAreaSqm / totalAreaSqm) * 100 : null;
  const countOcc = liveUnits.length > 0 ? (occupiedCount / liveUnits.length) * 100 : null;
  const currentOccupancyPct = clamp(reportedOcc ?? areaOcc ?? countOcc ?? 80, 0, 100);

  // Rent roll: real passing rents if present, else estimate from occupied GLA.
  let grossRentRoll = passingRentRoll;
  if (grossRentRoll <= 0 && occupiedAreaSqm > 0) {
    grossRentRoll = occupiedAreaSqm * FALLBACK_RENT_PER_SQM;
  } else if (grossRentRoll <= 0 && totalAreaSqm > 0) {
    grossRentRoll = totalAreaSqm * (currentOccupancyPct / 100) * FALLBACK_RENT_PER_SQM;
  }
  const rentRollIsEstimated = passingRentRoll <= 0 && grossRentRoll > 0;

  const currentNoi = grossRentRoll * (1 - BASELINE_OPEX_RATIO);
  const unitCount = property?.declaredUnitCount || property?.totalUnits || liveUnits.length || 0;
  const hasFinancials = passingRentRoll > 0 || totalAreaSqm > 0;
  const currentValue = currentNoi / MARKET_CAP_RATE;
  const ervAnnual = totalAreaSqm > 0 ? totalAreaSqm * MARKET_RENT_PER_SQM : grossRentRoll;

  // Average passing €/m² across occupied area, used to fill missing tenancy rents.
  const avgPassingPerSqm =
    occupiedAreaSqm > 0 && passingRentRoll > 0 ? passingRentRoll / occupiedAreaSqm : FALLBACK_RENT_PER_SQM;

  // Build a lease-by-lease rent roll, sorted by area (largest tenancies first).
  const rentRoll: RentRollLine[] = liveUnits
    .map((u) => {
      const area = typeof u.areaSqm === 'number' && u.areaSqm > 0 ? u.areaSqm : 0;
      const status = typeof u.status === 'string' ? u.status : 'AVAILABLE';
      const isOccupied = INCOME_STATUSES.has(status);
      const passingRent =
        isOccupied && typeof u.askingRent === 'number' && u.askingRent > 0
          ? u.askingRent
          : isOccupied
            ? area * avgPassingPerSqm
            : 0;
      const passingPerSqm = area > 0 && passingRent > 0 ? passingRent / area : 0;
      const ervPerSqm = MARKET_RENT_PER_SQM;
      const erv = area * ervPerSqm;
      const reversionPct = passingPerSqm > 0 ? (ervPerSqm / passingPerSqm - 1) * 100 : 0;
      const label =
        typeof u.unitIdentifier === 'string' && u.unitIdentifier.trim()
          ? u.unitIdentifier
          : u.id.slice(0, 6);
      return {
        unitId: u.id,
        label,
        tenant: isOccupied ? 'Let' : 'Vacant',
        areaSqm: Math.round(area),
        passingRent: Math.round(passingRent),
        passingPerSqm: Math.round(passingPerSqm),
        ervPerSqm: Math.round(ervPerSqm),
        erv: Math.round(erv),
        status,
        isOccupied,
        leaseExpiryYear: isOccupied ? Math.round(leaseExpiryFromStatus(status, u.id) * 10) / 10 : 0,
        reversionPct: Math.round(reversionPct),
      };
    })
    .sort((a, b) => b.areaSqm - a.areaSqm);

  // WAULT — rent-weighted average unexpired term over occupied tenancies.
  let waultNum = 0;
  let waultDen = 0;
  for (const line of rentRoll) {
    if (line.isOccupied && line.passingRent > 0) {
      waultNum += line.passingRent * line.leaseExpiryYear;
      waultDen += line.passingRent;
    }
  }
  const waultYears = waultDen > 0 ? Math.round((waultNum / waultDen) * 10) / 10 : 0;
  const reversionPct =
    grossRentRoll > 0 ? Math.round((ervAnnual / grossRentRoll - 1) * 100) : 0;

  return {
    currentNoi: Math.round(currentNoi),
    passingRentRoll: Math.round(grossRentRoll),
    currentOccupancyPct: Math.round(currentOccupancyPct),
    totalAreaSqm: Math.round(totalAreaSqm),
    occupiedAreaSqm: Math.round(occupiedAreaSqm),
    unitCount,
    occupiedUnitCount: occupiedCount,
    marketCapRatePct: Math.round(MARKET_CAP_RATE * 1000) / 10,
    currentValue: Math.round(currentValue),
    ervAnnual: Math.round(ervAnnual),
    rentRoll,
    waultYears,
    reversionPct,
    rentRollIsEstimated,
    hasFinancials,
  };
}

/** A lease-level edit to one rent-roll line (or an added/removed tenancy). */
export interface RentRollEdit {
  passingRent?: number; // € / yr
  areaSqm?: number;
  ervPerSqm?: number; // € / m² / yr
  leaseExpiryYear?: number;
  status?: string;
  tenant?: string;
  removed?: boolean; // soft-delete an existing tenancy
  added?: boolean; // a brand-new tenancy not backed by a unit
  label?: string; // unit label for an added tenancy
}

/** Recompute a rent-roll line's derived fields from its core (possibly edited) inputs. */
function finalizeRentRollLine(raw: {
  unitId: string;
  label: string;
  tenant: string;
  areaSqm: number;
  passingRent: number;
  ervPerSqm: number;
  leaseExpiryYear: number;
  status: string;
}): RentRollLine {
  const status = raw.status || 'AVAILABLE';
  const isOccupied = INCOME_STATUSES.has(status);
  const areaSqm = Math.max(0, Math.round(raw.areaSqm || 0));
  const passingRent = isOccupied ? Math.max(0, Math.round(raw.passingRent || 0)) : 0;
  const passingPerSqm = areaSqm > 0 && passingRent > 0 ? Math.round(passingRent / areaSqm) : 0;
  const ervPerSqm = Math.max(0, Math.round(raw.ervPerSqm || MARKET_RENT_PER_SQM));
  const erv = Math.round(areaSqm * ervPerSqm);
  const reversionPct = passingPerSqm > 0 ? Math.round((ervPerSqm / passingPerSqm - 1) * 100) : 0;
  const leaseExpiryYear = isOccupied ? Math.round((raw.leaseExpiryYear || 0) * 10) / 10 : 0;
  return {
    unitId: raw.unitId,
    label: raw.label,
    tenant: raw.tenant || (isOccupied ? 'Let' : 'Vacant'),
    areaSqm,
    passingRent,
    passingPerSqm,
    ervPerSqm,
    erv,
    status,
    isOccupied,
    leaseExpiryYear,
    reversionPct,
  };
}

/**
 * Apply lease-level edits (overrides by unitId, soft-removes, and added
 * tenancies) to a baseline's rent roll and recompute every aggregate it feeds:
 * GLA, occupancy, passing rent roll, NOI, value, ERV, WAULT and reversion.
 * Pure: returns a new baseline (or the same one when there are no edits).
 */
export function mergeRentRollEdits(
  baseline: AssetBaseline,
  edits: Record<string, RentRollEdit> | undefined | null,
): AssetBaseline {
  if (!edits || Object.keys(edits).length === 0) return baseline;

  const lines: RentRollLine[] = [];
  for (const line of baseline.rentRoll) {
    const e = edits[line.unitId];
    if (e?.removed) continue;
    if (!e) {
      lines.push(line);
      continue;
    }
    lines.push(
      finalizeRentRollLine({
        unitId: line.unitId,
        label: e.label ?? line.label,
        tenant: e.tenant ?? line.tenant,
        areaSqm: e.areaSqm ?? line.areaSqm,
        passingRent: e.passingRent ?? line.passingRent,
        ervPerSqm: e.ervPerSqm ?? line.ervPerSqm,
        leaseExpiryYear: e.leaseExpiryYear ?? line.leaseExpiryYear,
        status: e.status ?? line.status,
      }),
    );
  }
  for (const [key, e] of Object.entries(edits)) {
    if (!e.added || e.removed) continue;
    lines.push(
      finalizeRentRollLine({
        unitId: key,
        label: e.label ?? key,
        tenant: e.tenant ?? 'Let',
        areaSqm: e.areaSqm ?? 0,
        passingRent: e.passingRent ?? 0,
        ervPerSqm: e.ervPerSqm ?? MARKET_RENT_PER_SQM,
        leaseExpiryYear: e.leaseExpiryYear ?? 5,
        status: e.status ?? 'LEASED',
      }),
    );
  }
  lines.sort((a, b) => b.areaSqm - a.areaSqm);

  let totalAreaSqm = 0;
  let occupiedAreaSqm = 0;
  let passingRentRoll = 0;
  let occupiedCount = 0;
  let ervAnnual = 0;
  let waultNum = 0;
  let waultDen = 0;
  for (const l of lines) {
    totalAreaSqm += l.areaSqm;
    ervAnnual += l.erv;
    if (l.isOccupied) {
      occupiedCount += 1;
      occupiedAreaSqm += l.areaSqm;
      passingRentRoll += l.passingRent;
      if (l.passingRent > 0) {
        waultNum += l.passingRent * l.leaseExpiryYear;
        waultDen += l.passingRent;
      }
    }
  }
  const currentNoi = passingRentRoll * (1 - BASELINE_OPEX_RATIO);
  const currentValue = currentNoi / MARKET_CAP_RATE;
  const currentOccupancyPct =
    totalAreaSqm > 0 ? Math.round((occupiedAreaSqm / totalAreaSqm) * 100) : baseline.currentOccupancyPct;
  const waultYears = waultDen > 0 ? Math.round((waultNum / waultDen) * 10) / 10 : 0;
  const reversionPct = passingRentRoll > 0 ? Math.round((ervAnnual / passingRentRoll - 1) * 100) : 0;

  return {
    ...baseline,
    rentRoll: lines,
    totalAreaSqm: Math.round(totalAreaSqm),
    occupiedAreaSqm: Math.round(occupiedAreaSqm),
    passingRentRoll: Math.round(passingRentRoll),
    currentNoi: Math.round(currentNoi),
    currentValue: Math.round(currentValue),
    ervAnnual: Math.round(ervAnnual),
    currentOccupancyPct,
    occupiedUnitCount: occupiedCount,
    unitCount: lines.length,
    waultYears,
    reversionPct,
  };
}

/** Sensible starting scenario for this asset (capex sized off its GLA). */
export function deriveDefaultParams(baseline: AssetBaseline): ScenarioParams {
  const areaCapex = baseline.totalAreaSqm > 0 ? baseline.totalAreaSqm * REFURB_COST_PER_SQM : 12_500_000;
  return {
    capexEnvelope: Math.round(clamp(areaCapex, 2_000_000, 40_000_000) / 100_000) * 100_000,
    tenantMix: 'mixed',
    rentUpliftPct: 25,
    timelineMonths: 12,
    sustainability: 'silver',
    holdYears: 5,
    rentGrowthPct: 2,
    opexRatioPct: 20,
    exitCapRatePct: 5.25,
    acqCostPct: 2,
    sellingCostPct: 1.5,
    ltvPct: 55,
    interestRatePct: 5.5,
    arrangementFeePct: 1,
    mezzLtvPct: 0,
    mezzRatePct: 11,
    prefRatePct: 8,
    promotePct: 20,
  };
}

/** Project the full business plan from the live baseline + chosen parameters. */
export function deriveScenarioOutcomes(baseline: AssetBaseline, params: ScenarioParams): ScenarioOutcomes {
  const mix = TENANT_MIX_META[params.tenantMix];
  const sus = SUSTAINABILITY_META[params.sustainability];
  const currentNoi = Math.max(baseline.currentNoi, 1);
  const currentOcc = baseline.currentOccupancyPct;
  const hold = clamp(Math.round(params.holdYears), 3, 10);
  const opexRatio = clamp(params.opexRatioPct, 0, 60) / 100;
  const rentGrowth = clamp(params.rentGrowthPct, -5, 10) / 100;

  const stabilizedOcc = clamp(
    mix.targetOcc + sus.occBonus - params.rentUpliftPct * 0.28,
    Math.min(currentOcc, 60),
    98,
  );
  const delivery = params.timelineMonths;
  // Tenants decant during works, dropping from today's occupancy toward a floor.
  const decantFloor = clamp(currentOcc * 0.4, 12, currentOcc);
  const absorbMonths = clamp(16 / mix.absorption + params.rentUpliftPct * 0.4, 6, 30);

  const HORIZON = Math.max(36, hold * 12);
  const occupancyCurve: Point[] = [];
  for (let m = 0; m <= HORIZON; m += 1) {
    let y: number;
    if (m < delivery) {
      y = currentOcc - (currentOcc - decantFloor) * (m / Math.max(delivery, 1));
    } else {
      const t = (m - delivery) / absorbMonths;
      y = decantFloor + (stabilizedOcc - decantFloor) * sCurve(clamp(t, 0, 1.25));
    }
    occupancyCurve.push({ x: m, y: clamp(y, 0, 99) });
  }

  // --- Income build: occupancy + rent path over the hold --------------------
  const gla = baseline.totalAreaSqm > 0 ? baseline.totalAreaSqm : baseline.occupiedAreaSqm;
  const passingPerSqm =
    baseline.occupiedAreaSqm > 0 ? baseline.passingRentRoll / baseline.occupiedAreaSqm : FALLBACK_RENT_PER_SQM;
  const ervPerSqm = passingPerSqm * (1 + params.rentUpliftPct / 100); // market rent achieved on re-let
  const occ0 = currentOcc / 100;
  const stabOcc = stabilizedOcc / 100;
  const decantOcc = clamp(occ0 * 0.5, 0.2, occ0); // tenants leave during works
  const worksYears = clamp(Math.round(delivery / 12), 1, hold - 1);

  const occByYear = (y: number): number => {
    if (y <= 0) return occ0;
    if (y < worksYears) return occ0 - (occ0 - decantOcc) * (y / worksYears);
    if (y === worksYears) return decantOcc;
    return clamp(decantOcc + (stabOcc - decantOcc) * Math.min((y - worksYears) / 2, 1), 0, stabOcc);
  };
  // achieved €/m² blends passing (in place) into ERV (on re-let) post-works, then grows.
  const rentByYear = (y: number): number => {
    if (y <= 0) return passingPerSqm;
    if (y < worksYears) return passingPerSqm;
    const grown = ervPerSqm * Math.pow(1 + rentGrowth, Math.max(0, y - worksYears));
    return grown;
  };

  // sources & uses
  const purchase = baseline.currentValue;
  const acqCost = purchase * (clamp(params.acqCostPct, 0, 8) / 100);
  const subtotalCost = purchase + acqCost + params.capexEnvelope;
  const ltc = clamp(params.ltvPct, 0, 85) / 100;
  const mezzLtc = clamp(params.mezzLtvPct, 0, 30) / 100;
  const seniorLoan = subtotalCost * ltc;
  const mezzLoan = subtotalCost * mezzLtc;
  const totalDebt = seniorLoan + mezzLoan;
  const seniorFee = seniorLoan * (clamp(params.arrangementFeePct, 0, 5) / 100);
  const mezzFee = mezzLoan * (clamp(params.arrangementFeePct, 0, 5) / 100);
  const arrangementFee = seniorFee + mezzFee;
  const totalProjectCost = Math.max(subtotalCost + arrangementFee, 1);
  const equity = Math.max(totalProjectCost - totalDebt, 1);
  const seniorInterest = seniorLoan * (clamp(params.interestRatePct, 0, 20) / 100);
  const mezzInterest = mezzLoan * (clamp(params.mezzRatePct, 0, 25) / 100);
  const annualInterest = seniorInterest + mezzInterest; // interest-only

  // exit
  const exitCap = clamp(params.exitCapRatePct, 3, 12) / 100;
  const capexByYear = (y: number): number => (y >= 1 && y <= worksYears ? params.capexEnvelope / worksYears : 0);
  const noiByYear = (y: number): number => gla * rentByYear(y) * occByYear(y) * (1 - opexRatio);
  const stabilizedNoi = noiByYear(hold);
  const exitValue = stabilizedNoi / exitCap; // gross development value
  const netExit = exitValue * (1 - clamp(params.sellingCostPct, 0, 5) / 100);

  const annualCashflow: AnnualCF[] = [];
  const unleveredCF: number[] = [];
  const leveredCF: number[] = [];
  let minIcr = Number.POSITIVE_INFINITY;
  for (let y = 0; y <= hold; y += 1) {
    const occ = occByYear(y);
    const rent = rentByYear(y);
    const gpr = y === 0 ? 0 : gla * rent; // gross potential at full occupancy
    const vacancyLoss = y === 0 ? 0 : gpr * (1 - occ);
    const grossRent = gpr - vacancyLoss;
    const opex = y === 0 ? 0 : grossRent * opexRatio;
    const noi = y === 0 ? 0 : grossRent - opex;
    const capex = capexByYear(y);
    const noiAfterCapex = noi - capex;
    const intr = y === 0 ? 0 : annualInterest;
    const fees = y === 0 ? arrangementFee : 0;
    const exitIn = y === hold ? netExit : 0;
    const debtRepay = y === hold ? totalDebt : 0;
    const unlev = y === 0 ? -(purchase + acqCost) : noiAfterCapex + exitIn;
    const lev =
      y === 0
        ? totalDebt - (purchase + acqCost + arrangementFee)
        : noiAfterCapex - intr + exitIn - debtRepay;
    const icr = intr > 0 ? noi / intr : 0;
    // Stabilised cover only — the works-period dip is funded by an interest reserve.
    if (y > worksYears && y <= hold && intr > 0) minIcr = Math.min(minIcr, icr);
    unleveredCF.push(unlev);
    leveredCF.push(lev);
    annualCashflow.push({
      year: y,
      occupancyPct: Math.round(occ * 100),
      rentPerSqm: Math.round(rent),
      gpr: Math.round(gpr),
      vacancyLoss: Math.round(vacancyLoss),
      opex: Math.round(opex),
      noi: Math.round(noi),
      capex: Math.round(capex),
      noiAfterCapex: Math.round(noiAfterCapex),
      interest: Math.round(intr),
      debtFees: Math.round(fees),
      unlevered: Math.round(unlev),
      levered: Math.round(lev),
      icr: Math.round(icr * 100) / 100,
    });
  }
  if (!Number.isFinite(minIcr)) minIcr = 0;

  const unleveredIrr = clamp(irr(unleveredCF) * 100, -99, 200);
  const leveredIrr = clamp(irr(leveredCF) * 100, -99, 200);
  const distributions = leveredCF.reduce((s, v) => (v > 0 ? s + v : s), 0);
  const contributions = leveredCF.reduce((s, v) => (v < 0 ? s - v : s), 0);
  const equityMultiple = contributions > 0 ? distributions / contributions : 0;
  const profitEur = exitValue - totalProjectCost;
  const profitOnCostPct = (profitEur / totalProjectCost) * 100;
  const yieldOnCostPct = (stabilizedNoi / totalProjectCost) * 100;
  const exitLtvPct = exitValue > 0 ? (totalDebt / exitValue) * 100 : 0;
  const waterfall = runWaterfall(leveredCF, params.prefRatePct, params.promotePct);

  return {
    occupancyCurve,
    annualCashflow,
    holdYears: hold,
    irrPct: Math.round(unleveredIrr * 10) / 10,
    leveredIrrPct: Math.round(leveredIrr * 10) / 10,
    timeToDeliveryMonths: delivery,
    stabilizedOccupancyPct: Math.round(stabOcc * 100),
    stabilizedNoi: Math.round(stabilizedNoi),
    currentNoi: Math.round(currentNoi),
    totalProjectCost: Math.round(totalProjectCost),
    acquisitionCost: Math.round(acqCost),
    subtotalCost: Math.round(subtotalCost),
    arrangementFee: Math.round(arrangementFee),
    exitValue: Math.round(exitValue),
    netExitValue: Math.round(netExit),
    profitEur: Math.round(profitEur),
    profitOnCostPct: Math.round(profitOnCostPct * 10) / 10,
    yieldOnCostPct: Math.round(yieldOnCostPct * 10) / 10,
    equity: Math.round(equity),
    peakDebt: Math.round(totalDebt),
    peakDebtSenior: Math.round(seniorLoan),
    peakDebtMezz: Math.round(mezzLoan),
    ltcPct: Math.round((totalDebt / Math.max(totalProjectCost, 1)) * 1000) / 10,
    exitLtvPct: Math.round(exitLtvPct * 10) / 10,
    minIcr: Math.round(minIcr * 100) / 100,
    annualInterest: Math.round(annualInterest),
    equityMultiple: Math.round(equityMultiple * 100) / 100,
    waultYears: baseline.waultYears,
    reversionPct: baseline.reversionPct,
    waterfall,
  };
}
